/**
 * @author Lloyd Evans
 */
import { Application, Renderer, utils, Loader, LoaderResource, Texture, BaseTexture, RENDERER_TYPE } from "pixi.js";
import { AssetConfig } from "./AssetConfig";
import { AssetLoaderEvents } from "./AssetLoaderEvents";
import { LoaderMiddleware, LoaderMiddlewarePre } from "./LoaderMiddleware";
import { ScaleVariant } from "./ScaleVariant";

/**
 *
 */
export class AssetLoader extends utils.EventEmitter {
  /**
   */
  public static baseUrl: string = "";

  /**
   */
  public static spineResources: LoaderResource[] = [];

  /**
   * PIXI.Application instance
   */
  public app: Application;

  /**
   * The currently loaded tag filtered assets.
   */
  public current: AssetConfig[] = [];

  /**
   * Current tags.
   */
  public tags: string[];

  /**
   * Asset bank.
   */
  public bank: AssetConfig[] = [];

  /**
   * Should gl textures be uploaded on load?
   */
  public uploadGlTextures: boolean = true;

  /**
   * Enabled debug output.
   */
  public debug: boolean = true;

  /**
   *
   */
  public scale: ScaleVariant;

  /**
   */
  public scaleVariantList: ScaleVariant[];

  /**
   *
   */
  private loadInProgress: boolean = false;

  /**
   */
  public static removeExtension(value: string): string {
    return value.replace(/\.[^/.]+$/, "");
  }

  /**
   * Check an array contains a texture
   */
  public static containsTexture(target: Texture[], texture: Texture): boolean {
    return target.some((el: any) => el.uid === (texture as any).uid);
  }

  /**
   *
   */
  public static updateGlTextures(app: Application) {
    if (app.renderer.type === RENDERER_TYPE.WEBGL) {
      const renderer = app.renderer as Renderer;
      for (const key in utils.BaseTextureCache) {
        const element = utils.BaseTextureCache[key];
        if (typeof element._glTextures[0] === "undefined") {
          renderer.plugins.prepare.upload(element);
        }
      }
    }
  }

  /**
   */
  public static runGarbageCollector(app: Application) {
    if (app.renderer.type === RENDERER_TYPE.WEBGL) {
      const renderer = app.renderer as Renderer;
      renderer.textureGC.run();
    }
  }

  /**
   */
  public isAssetTexture(assetConfig: AssetConfig, texture: Texture) {
    let found = false;
    if (texture.baseTexture && texture.baseTexture.resource && texture.baseTexture.resource.url) {
      for (let i = 0; i < assetConfig.assets.length; i++) {
        const asset = assetConfig.assets[i];
        if (
          AssetLoader.removeExtension(texture.baseTexture.resource.url) ===
          AssetLoader.baseUrl + AssetLoader.removeExtension(asset)
        ) {
          found = true;
          break;
        }
      }
    }
    return found;
  }

  /**
   * @param app
   */
  constructor(app: Application) {
    super();
    this.app = app;
    for (const middleware of LoaderMiddleware) this.app.loader.use(middleware);
    for (const middleware of LoaderMiddlewarePre) this.app.loader.pre(middleware);
  }

  /**
   * Load assets with tags, and unload existing assets
   * without the tags.
   */
  public loadAssetsWithTags(tags: string[]) {
    if (!this.loadInProgress) {
      this.addAssetsWithTags(tags);
      this.app.loader.load();
      this.loadInProgress = true;
    } else {
      if (this.debug) {
        console.log(`AssetLoader.loadAssetsWithTags: Load already in progress.`);
      }
    }

    return this;
  }

  public addAssetsWithTags(tags: string[]) {
    this.emit(AssetLoaderEvents.ADD);

    const newAssets: AssetConfig[] = this.getAssetsWithTags(tags);

    // Filter already loaded.
    let load: AssetConfig[] = [].concat(newAssets);
    for (let i = 0; i < this.current.length; i++) {
      load = load.filter(el => el.assets.join() !== this.current[i].assets.join());
    }

    let unload: AssetConfig[] = [].concat(this.current);
    for (let i = 0; i < newAssets.length; i++) {
      unload = unload.filter(el => el.assets.join() !== newAssets[i].assets.join());
    }

    // Unload them.
    for (let i = 0; i < unload.length; i++) {
      if (unload[i].isSoundAsset) unload[i].sound.unload();
      else this.unloadBaseTexture(unload[i]);
    }
    AssetLoader.runGarbageCollector(this.app);

    // Update latest data for next load.
    this.current = newAssets;
    this.tags = tags;

    if (load.length > 0) {
      // Reset and start Pixi loader.
      // this.app.loader.reset();
      for (let i = 0; i < load.length; i++) this.addAssetConfigToLoader(load[i]);
      this.app.loader.on("progress", this.onProgress, this);
      this.app.loader.on("complete", this.onComplete, this);
    } else {
      this.emit(AssetLoaderEvents.COMPLETE, this.app.loader);
    }

    if (this.debug) {
      console.info(
        `AssetLoader.addAssetsWithTags:`,
        `- Unload: ${unload.length}`,
        `- Load: ${load.length}`,
        `[${tags.toString()}]`
      );
    }

    return this;
  }

  /**
   *
   * @param tags
   */
  protected getAssetsWithTags(tags: string[]): AssetConfig[] {
    const assets = [];
    for (let i = 0; i < this.bank.length; i++) {
      let found = false;
      for (let t = 0; t < tags.length; t++) {
        if (this.bank[i].tags.indexOf(tags[t]) !== -1) found = true;
      }
      if (found && !this.bank[i].dontLoad) assets.push(this.bank[i]);
    }
    return assets;
  }

  /**
   */
  public checkTagsNeedToLoad(tags: string[]) {
    this.emit(AssetLoaderEvents.CHECK_TAGS, tags);
    let load = this.getAssetsWithTags(tags);
    load = load.filter(el => !el.dontLoad);
    for (let i = 0; i < this.current.length; i++)
      load = load.filter(el => el.assets.join() !== this.current[i].assets.join());
    return load.length > 0;
  }

  /**
   */
  protected unloadBaseTexture(asset: AssetConfig) {
    if (asset.spineJson) {
      AssetLoader.spineResources = AssetLoader.spineResources.filter(el => el.name !== asset.name);
    }

    let destroyed = [];
    for (const key in utils.TextureCache) {
      const texture = utils.TextureCache[key];

      if (key.match("_atlas_page_")) {
        if (key.indexOf(asset.name) === 0) {
          Texture.removeFromCache(texture);
          texture.destroy(true);
          continue;
        }
      }

      if (this.isAssetTexture(asset, texture)) {
        if (!AssetLoader.containsTexture(destroyed, texture.baseTexture))
          destroyed.push(texture.baseTexture);
        Texture.removeFromCache(texture);
        texture.destroy();
      }
    }

    for (let i = 0; i < destroyed.length; i++) {
      BaseTexture.removeFromCache(destroyed[i]);
      destroyed[i].destroy(true);
    }
  }

  /**
   * Load progress.
   */
  protected onProgress(loader: Loader, resource: LoaderResource) {
    if (this.uploadGlTextures) AssetLoader.updateGlTextures(this.app);
    this.emit(AssetLoaderEvents.PROGRESS, loader, resource);
  }

  /**
   * Load complete.
   */
  protected onComplete(loader: Loader) {
    this.loadInProgress = false;
    this.app.loader.off("complete", this.onComplete, this);
    this.app.loader.off("progress", this.onProgress, this);
    this.emit(AssetLoaderEvents.COMPLETE, loader);
  }

  /**
   *
   * @param scaleVariantKey
   */
  public setScaleVariant(scaleVariantKey: string) {
    if (this.debug) console.log(`PixiApp.setScaleVariant`, scaleVariantKey);
    let currentScale: ScaleVariant | undefined;
    for (let scaleVariant of this.scaleVariantList)
      if (scaleVariant.key === scaleVariantKey) {
        currentScale = scaleVariant;
        break;
      }
    if (typeof currentScale === "undefined") console.log(`Loader - no variant ${scaleVariantKey}`);
    else {
      this.scale = currentScale;
    }
    return currentScale;
  }

  /**
   */
  public parseManifest(manifest: any) {
    // this.bank = [];
    for (const groupName in manifest) {
      const group = manifest[groupName];
      for (const atlasName in group) {
        const asset = group[atlasName];
        if (asset[this.scale.key].length > 0)
          this.bank.push({
            name: atlasName,
            assets: asset[this.scale.key],
            spineJson: asset.spineJson,
            spineScale: asset.spineScale,
            tags: asset.tags || []
          });
      }
    }
  }

  /**
   * @param tpconfig
   */
  public parseTpConfig(tpconfig: any, scaleKey: string) {
    this.scaleVariantList = [];
    for (const key in tpconfig.scaleVariants) {
      this.scaleVariantList.push({
        value: tpconfig.scaleVariants[key],
        key: key
      });
    }
    this.scale = this.scaleVariantList[scaleKey];
    this.setScaleVariant(scaleKey);
  }

  /**
   *
   * @param assetConfig
   */
  public addAssetConfigToLoader(assetConfig: AssetConfig) {
    if (assetConfig.spineJson) {
      this.app.loader.add(assetConfig.name, AssetLoader.baseUrl + assetConfig.spineJson, {
        metadata: {
          spineAtlasFile: AssetLoader.baseUrl + assetConfig.assets[0],
          spineScale: assetConfig.spineScale
        }
      });
    } else if (assetConfig.sound) {
      this.app.loader.add(assetConfig.name, "", { metadata: assetConfig });
    } else if (assetConfig.isSoloImg) {
      this.app.loader.add(assetConfig.name, AssetLoader.baseUrl + assetConfig.assets[0]);
    } else {
      for (let i = 0; i < assetConfig.assets.length; i++)
        this.app.loader.add(AssetLoader.baseUrl + assetConfig.assets[i]);
    }
  }
}
